import { useCardStore } from '@/stores/useCardStore';
import { CardLink } from '@/types/card';
import { handleError } from '@/utils/handleError';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const useCardLinks = () => {
  const { card, updateCardLinks } = useCardStore();
  const [links, setLinks] = useState<CardLink[]>([]);
  const [hasDirectLink, setHasDirectLink] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (card?.links) {
      const updatedLinks = card.links.map(link => ({
        ...link,
        is_active: link.is_active !== undefined ? link.is_active : true,
        is_direct: link.is_direct,
      }));

      const directLink = updatedLinks.find(link => link.is_direct);
      setHasDirectLink(!!directLink);
      setLinks(updatedLinks);
    }
  }, [card]);

  const handleUpdateLinks = async (updatedLinks: CardLink[]) => {
    const originalLinks = [...links];
    try {
      setLinks(updatedLinks);
      if (card) {
        await updateCardLinks(card.slug, updatedLinks);
        setLinks(updatedLinks);
      }
    } catch (error) {
      setLinks(originalLinks);
      handleError(error, undefined, navigate);
    }
  };

  const handleToggleDirectLink = (checked: boolean) => {
    if (!links.length) return;

    let updatedLinks;
    const existingDirectLinkIndex = links.findIndex(link => link.is_direct);

    if (!checked) {
      updatedLinks = links.map(link => ({
        ...link,
        is_direct: false,
      }));
      setHasDirectLink(false);
    } else {
      // If there's no existing direct link, find the first active link
      if (existingDirectLinkIndex === -1) {
        // Find the first active link index
        const firstActiveLinkIndex = links.findIndex(link => link.is_active);

        // If there's at least one active link
        if (firstActiveLinkIndex !== -1) {
          updatedLinks = links.map((link, index) => ({
            ...link,
            is_direct: index === firstActiveLinkIndex,
          }));
        } else {
          // If no active links, don't set any as direct
          updatedLinks = [...links];
        }
      } else {
        // If there's an existing direct link, ensure it's active and remains the direct link
        updatedLinks = links.map((link, index) => ({
          ...link,
          is_direct: index === existingDirectLinkIndex,
          is_active: index === existingDirectLinkIndex ? true : link.is_active,
        }));
      }
      setHasDirectLink(true);
    }

    if (card) {
      handleUpdateLinks(updatedLinks);
    }
  };

  return {
    links,
    hasDirectLink,
    setHasDirectLink,
    handleUpdateLinks,
    handleToggleDirectLink,
  };
};
